import { Clock, AlertOctagon, AlertTriangle, ShieldCheck } from "lucide-react";

/**
 * Prominent runway / survival horizon panel.
 *
 * Sits at the top of the Findings stack so the owner (or analyst) sees the
 * answer to "how long do I have?" before anything else. Severity colouring
 * mirrors the rest of the design system:
 *   ok       → green   (calm; the business has time)
 *   warning  → amber   (act this quarter)
 *   critical → red     (act this month)
 *
 * Renders an array of `key_actions`, each with a concrete deadline label,
 * so the urgency communicated by the headline turns into a checklist the
 * owner can act on this week.
 */
const TONE = {
  ok: {
    icon: ShieldCheck,
    cardBg: "bg-[#2C5C43]/5",
    cardBorder: "border-[#2C5C43]/30",
    accent: "text-[#2C5C43]",
    badge: "bg-[#2C5C43]/15 text-[#2C5C43]",
    badgeLabel: "Healthy runway",
  },
  warning: {
    icon: AlertTriangle,
    cardBg: "bg-[#9c6b1d]/5",
    cardBorder: "border-[#9c6b1d]/30",
    accent: "text-[#9c6b1d]",
    badge: "bg-[#9c6b1d]/15 text-[#9c6b1d]",
    badgeLabel: "Act this quarter",
  },
  critical: {
    icon: AlertOctagon,
    cardBg: "bg-[#A84632]/5",
    cardBorder: "border-[#A84632]/40",
    accent: "text-[#A84632]",
    badge: "bg-[#A84632]/15 text-[#A84632]",
    badgeLabel: "Act this month",
  },
};

const PRIORITY_DOT = {
  critical: "bg-[#A84632]",
  high: "bg-[#9c6b1d]",
  medium: "bg-[#1F382E]",
  low: "bg-[#5C665D]",
};

export default function SurvivalTimeline({ timeline }) {
  if (!timeline) return null;
  const t = TONE[timeline.severity] || TONE.ok;
  const Icon = t.icon;
  const months = timeline.cash_runway_months;

  return (
    <div
      className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-6 sm:p-7 print-keep`}
      data-testid="survival-timeline"
    >
      <div className="flex items-start gap-4">
        <div
          className={`h-10 w-10 rounded-full bg-white border ${t.cardBorder} flex items-center justify-center shrink-0`}
        >
          <Icon size={18} strokeWidth={1.5} className={t.accent} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#5C665D]">
              Runway &amp; Timeline
            </p>
            <span
              className={`text-[10px] uppercase tracking-[0.16em] rounded-full px-2 py-0.5 ${t.badge}`}
              data-testid="survival-severity-badge"
            >
              {t.badgeLabel}
            </span>
          </div>
          <h2
            className="font-serif-display text-2xl sm:text-3xl text-[#1F382E] tracking-tight leading-tight mt-2"
            data-testid="survival-headline"
          >
            {timeline.headline}
          </h2>
          <p className="text-[14.5px] text-[#1A1A1A] leading-relaxed mt-3 max-w-3xl">
            {timeline.detail}
          </p>

          {(months != null || timeline.monthly_burn || timeline.monthly_operating_cost) && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {months != null && (
                <Stat
                  label="Cash runway"
                  value={`${months.toFixed(1)} mo`}
                  testid="survival-runway"
                />
              )}
              {timeline.monthly_burn != null && (
                <Stat
                  label="Monthly burn"
                  value={fmtNumber(timeline.monthly_burn)}
                  testid="survival-burn"
                />
              )}
              {timeline.monthly_operating_cost != null && (
                <Stat
                  label="Monthly opex"
                  value={fmtNumber(timeline.monthly_operating_cost)}
                  testid="survival-opex"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {Array.isArray(timeline.key_actions) && timeline.key_actions.length > 0 && (
        <div className="mt-6 pt-5 border-t border-dashed border-[#E5E0D8]">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665D]">
            Deadlines that matter
          </p>
          <ol className="mt-3 space-y-2.5" data-testid="survival-actions">
            {timeline.key_actions.map((act, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm text-[#1A1A1A] leading-relaxed"
                data-testid={`survival-action-${i}`}
              >
                <Clock
                  size={14}
                  strokeWidth={1.5}
                  className={`mt-0.5 shrink-0 ${t.accent}`}
                />
                <span className="flex-1">
                  <span className="font-medium text-[#1F382E]">{act.deadline_label}</span>{" "}
                  — {act.action}
                </span>
                <span
                  className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${PRIORITY_DOT[act.priority] || PRIORITY_DOT.medium}`}
                  aria-label={`${act.priority} priority`}
                />
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2.5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#5C665D]">{label}</p>
      <p className="font-serif-display text-xl text-[#1F382E] tabular-nums leading-none mt-1">
        {value}
      </p>
    </div>
  );
}

function fmtNumber(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return `${Math.round(n)}`;
}
